/**
 * Cliente do Open-Meteo (clima atual + previsão + geocoding).
 * Totalmente gratuito, sem necessidade de API key, ilimitado pra uso não comercial.
 */

export interface GeocodingResult {
  id: number;
  name: string;
  admin1?: string;
  country?: string;
  latitude: number;
  longitude: number;
}

interface GeocodingResponse {
  results?: GeocodingResult[];
}

export interface CurrentWeather {
  temperature: number;
  weatherCode: number;
  windSpeed?: number;
  isDay: boolean;
  time: string;
}

export interface ForecastDay {
  /** ISO date YYYY-MM-DD */
  date: string;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
}

export interface WeatherSnapshot {
  current: CurrentWeather;
  daily: ForecastDay[];
}

interface ForecastResponse {
  current?: {
    time: string;
    temperature_2m: number;
    weather_code: number;
    wind_speed_10m?: number;
    is_day: 0 | 1;
  };
  daily?: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
  };
}

const GEOCODE_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const REVERSE_URL = 'https://geocoding-api.open-meteo.com/v1/reverse';
const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';

export async function geocodeCity(query: string, signal?: AbortSignal): Promise<GeocodingResult[]> {
  const q = query.trim();
  if (!q) return [];
  const url = `${GEOCODE_URL}?name=${encodeURIComponent(q)}&count=8&language=pt&format=json`;
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`Geocoding ${r.status}`);
  const data = (await r.json()) as GeocodingResponse;
  return data.results ?? [];
}

export async function reverseGeocode(
  latitude: number,
  longitude: number,
  signal?: AbortSignal,
): Promise<GeocodingResult | null> {
  const url = `${REVERSE_URL}?latitude=${latitude}&longitude=${longitude}&count=1&language=pt&format=json`;
  try {
    const r = await fetch(url, { signal });
    if (!r.ok) return null;
    const data = (await r.json()) as GeocodingResponse;
    return data.results?.[0] ?? null;
  } catch {
    return null;
  }
}

export async function fetchWeather(
  latitude: number,
  longitude: number,
  signal?: AbortSignal,
): Promise<WeatherSnapshot> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    current: 'temperature_2m,weather_code,wind_speed_10m,is_day',
    daily: 'weather_code,temperature_2m_max,temperature_2m_min',
    forecast_days: '5',
    timezone: 'auto',
  });
  const r = await fetch(`${FORECAST_URL}?${params}`, { signal });
  if (!r.ok) throw new Error(`Weather ${r.status}`);
  const data = (await r.json()) as ForecastResponse;
  if (!data.current) throw new Error('Resposta sem `current`');
  const current: CurrentWeather = {
    temperature: data.current.temperature_2m,
    weatherCode: data.current.weather_code,
    windSpeed: data.current.wind_speed_10m,
    isDay: data.current.is_day === 1,
    time: data.current.time,
  };
  const daily: ForecastDay[] = data.daily
    ? data.daily.time.map((t, i) => ({
        date: t,
        weatherCode: data.daily!.weather_code[i],
        tempMax: data.daily!.temperature_2m_max[i],
        tempMin: data.daily!.temperature_2m_min[i],
      }))
    : [];
  return { current, daily };
}

export function describeWeatherCode(code: number): string {
  if (code === 0) return 'Céu limpo';
  if (code === 1) return 'Quase limpo';
  if (code === 2) return 'Parcialmente nublado';
  if (code === 3) return 'Nublado';
  if (code === 45 || code === 48) return 'Nevoeiro';
  if (code >= 51 && code <= 57) return 'Garoa';
  if (code >= 61 && code <= 67) return 'Chuva';
  if (code >= 71 && code <= 77) return 'Neve';
  if (code >= 80 && code <= 82) return 'Pancadas de chuva';
  if (code >= 85 && code <= 86) return 'Pancadas de neve';
  if (code >= 95) return 'Trovoada';
  return 'Indefinido';
}
